import React, { useState, useEffect } from 'react';
import './UserTermsHistory.css';

const UserTermsHistory = () => {
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState({});
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchAgreements();
  }, []);

  const fetchAgreements = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/terms/agreements', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setAgreements(data.agreements || []);
      }
    } catch (error) {
      console.error('Error fetching agreements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePDF = async (agreementId) => {
    setGenerating(prev => ({ ...prev, [agreementId]: true }));
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/terms/agreements/${agreementId}/pdf`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (response.ok) {
        setMessage('PDF generated successfully!');
        fetchAgreements(); // Refresh to show new PDF
      } else {
        setMessage(`Error: ${data.error}`);
      }
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    } finally {
      setGenerating(prev => ({ ...prev, [agreementId]: false }));
    }
  };

  const handleDownloadPDF = async (pdfId, agreementId) => {
    try {
      const token = localStorage.getItem('token');
      
      if (!pdfId) {
        // Generate first if not exists
        await handleGeneratePDF(agreementId);
        return;
      }

      window.open(`/api/terms/pdf/${pdfId}/download?token=${token}`, '_blank');
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    }
  };

  const handleViewPDF = async (pdfId, agreementId) => {
    try {
      const token = localStorage.getItem('token');
      
      if (!pdfId) {
        // Generate first if not exists
        await handleGeneratePDF(agreementId);
        return;
      }

      window.open(`/api/terms/pdf/${pdfId}/view?token=${token}`, '_blank');
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="terms-history-loading">
        <div className="spinner"></div>
        <p>Loading your agreements...</p>
      </div>
    );
  }

  return (
    <div className="user-terms-history">
      <div className="history-header">
        <h2>📄 My Terms & Conditions History</h2>
        <p className="subtitle">View and download your accepted terms documents</p>
      </div>

      {message && (
        <div className="history-message">
          {message}
          <button onClick={() => setMessage('')}>×</button>
        </div>
      )}

      {agreements.length === 0 ? (
        <div className="no-agreements">
          <div className="empty-icon">📋</div>
          <h3>No agreements yet</h3>
          <p>You haven't accepted any terms & conditions yet.</p>
        </div>
      ) : (
        <div className="uth-agreements-list">
          {agreements.map(agreement => (
            <div key={agreement.id} className="uth-agreement-card">
              <div className="agreement-header">
                <div className="agreement-title">
                  <h3>{agreement.title}</h3>
                  <span className="version-badge">Version {agreement.version}</span>
                </div>
                <div className="agreement-date">
                  Accepted: {new Date(agreement.agreed_at).toLocaleString()}
                </div>
              </div>

              <div className="agreement-details">
                <div className="detail-item">
                  <span className="detail-label">Effective Date:</span>
                  <span className="detail-value">
                    {new Date(agreement.effective_date).toLocaleDateString()}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">IP Address:</span>
                  <span className="detail-value">
                    <code>{agreement.ip_address}</code>
                  </span>
                </div>
                {agreement.pdf_path && (
                  <div className="detail-item">
                    <span className="detail-label">PDF Status:</span>
                    <span className="detail-value pdf-available">
                      ✓ Generated {agreement.download_count > 0 && `(Downloaded ${agreement.download_count}x)`}
                    </span>
                  </div>
                )}
              </div>

              <div className="agreement-actions">
                {agreement.pdf_id ? (
                  <>
                    <button 
                      className="btn-action btn-view"
                      onClick={() => handleViewPDF(agreement.pdf_id, agreement.id)}
                    >
                      <span className="btn-icon">👁️</span>
                      View PDF
                    </button>
                    <button 
                      className="btn-action btn-download"
                      onClick={() => handleDownloadPDF(agreement.pdf_id, agreement.id)}
                    >
                      <span className="btn-icon">⬇️</span>
                      Download
                    </button>
                  </>
                ) : (
                  <button 
                    className="btn-action btn-generate"
                    onClick={() => handleGeneratePDF(agreement.id)}
                    disabled={generating[agreement.id]}
                  >
                    {generating[agreement.id] ? (
                      <>
                        <span className="btn-spinner"></span>
                        Generating...
                      </>
                    ) : (
                      <>
                        <span className="btn-icon">📄</span>
                        Generate PDF
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="history-footer">
        <p className="footer-note">
          📌 All documents are digitally signed and legally binding. Keep them for your records.
        </p>
      </div>
    </div>
  );
};

export default UserTermsHistory;
